import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, OnInit, Output, inject } from '@angular/core';
import { V3MemberInfoDialogComponent } from '@app/app/page-modules/admin/components/members/admin-member-list/v3-member-info-dialog/v3-member-info-dialog.component';
import { MemberInfoDialogData } from '@app/app/page-modules/admin/components/members/model/members.model';
import {
  UpdateCardPaginationFilters,
  UpdateMemberPaginationFilters,
  UpdatePlaylistPaginationFilters,
} from '@app/app/page-modules/admin/components/members/store/members.action';
import { PaginationFilter } from '@app/app/page-modules/admin/components/members/store/members.state.model';
import { CheckedMembersData, Members, MembersSearchOrder } from '@app/app/shared/models/admin/members.model';
import { PtlTableColumn, PtlTableSort } from '@app/app/shared/styleguide/components/table/ptl-table.component';
import { marker } from '@jsverse/transloco-keys-manager/marker';
import { LucideEllipsisVertical, LucideUsers } from '@lucide/angular';
import { TranslocoService } from '@ngneat/transloco';
import { takeUntil } from 'rxjs/operators';
import { AdminMemberTableComponent } from '../admin-member-table/admin-member-table.component';

export const MEMBER_SORT_KEYS: readonly string[] = ['firstName', 'lastName', 'lastActiveOn', 'createdAt'];

@Component({
  selector: 'ptl-v3-admin-member-list',
  templateUrl: './v3-admin-member-list.component.html',
  styleUrls: ['./v3-admin-member-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class V3AdminMemberListComponent extends AdminMemberTableComponent implements OnInit, OnChanges {
  @Input() cardUid: string;
  @Input() playlistUid: string;

  @Output() refreshList = new EventEmitter<number>();

  readonly LucideEllipsisVertical = LucideEllipsisVertical;
  readonly LucideUsers = LucideUsers;

  tableColumns: PtlTableColumn[] = [];
  tableSort: PtlTableSort | null = null;

  private readonly transloco = inject(TranslocoService);
  private selectedKeysCache: string[] = [];

  get defaultTable(): AdminMemberTableComponent {
    return this;
  }

  get selectedMemberIds(): string[] {
    const ids = this.checkedMembers.map((member) => member._id);
    if (ids.length !== this.selectedKeysCache.length || ids.some((id, index) => id !== this.selectedKeysCache[index])) {
      this.selectedKeysCache = ids;
    }
    return this.selectedKeysCache;
  }

  override ngOnInit(): void {
    super.ngOnInit();
    this.buildTableColumns();
    this.syncTableSort();
  }

  override ngOnChanges(): void {
    super.ngOnChanges();
    this.buildTableColumns();
  }

  setIsLoaderActive(active: boolean) {
    this.isLoaderActive = active;
  }

  setPageSize(size: number) {
    this.pageSize = size;
  }

  setCurrentPage(page: number) {
    this.currentPage = page;
  }

  override onPageChangeEvent(event: { currentPage: number; pageSize: number }): void {
    super.onPageChangeEvent(event);
    this.persistPagination();
  }

  override onSizeChange(size: number): void {
    super.onSizeChange(size);
    this.persistPagination();
  }

  override changeListOrdering(orderBy: string): void {
    super.changeListOrdering(orderBy);
    this.syncTableSort();
    this.persistPagination();
  }

  onSortChange(sort: PtlTableSort | null): void {
    const key = sort?.key ?? this.defaultOrderBy;
    if (key) {
      this.changeListOrdering(key);
    }
  }

  onSelectedKeysChange(keys: unknown[]): void {
    const ids = keys as string[];
    const selectedIds = new Set(ids);
    const existing = new Map(this.checkedMembers.map((member) => [member._id, member] as [string, CheckedMembersData]));

    this.checkedMembers = ids.map(
      (id) => existing.get(id) ?? { _id: id, reviewUid: this.members?.find((member) => member._id === id)?.review?._id },
    );

    for (const member of this.members ?? []) {
      member.checked = selectedIds.has(member._id);
    }

    this.updateAllUsersChecked();
    this.membersChecked.emit(this.checkedMembers);
  }

  getMemberInitials(firstName: string, lastName: string): string {
    const first = firstName?.trim().charAt(0) ?? '';
    const last = lastName?.trim().charAt(0) ?? '';
    return (first + last).toUpperCase();
  }

  getRoleLabel(roles: string[]): string {
    return roles?.length ? roles[0] : '';
  }

  isMemberActive(member: Members): boolean {
    const lastActive = member?.lastActiveOn ? new Date(member.lastActiveOn).getTime() : 0;
    if (!lastActive) {
      return false;
    }
    const oneWeekMs = 7 * 24 * 60 * 60 * 1000;
    return Date.now() - lastActive <= oneWeekMs;
  }

  private buildTableColumns(): void {
    const columns: PtlTableColumn[] = [];

    if (this.isColumnEnabled('FIRST_NAME')) {
      columns.push({
        key: 'firstName',
        label: this.translate(marker('translations.global.label.firstName')),
        sortable: true,
        width: '11rem',
        truncate: true,
      });
    }
    if (this.isColumnEnabled('LAST_NAME')) {
      columns.push({
        key: 'lastName',
        label: this.translate(marker('translations.global.label.lastName')),
        sortable: true,
        width: '9rem',
        truncate: true,
      });
    }
    if (this.isColumnEnabled('EMAIL')) {
      columns.push({ key: 'email', label: this.translate(marker('translations.global.label.email')), width: '12rem', truncate: true });
    }
    if (this.isColumnEnabled('ROLES')) {
      columns.push({ key: 'roles', label: this.translate(marker('translations.global.label.role')), width: '6rem', truncate: true });
    }
    if (this.isColumnEnabled('GROUPS')) {
      columns.push({ key: 'groups', label: this.translate(marker('translations.global.label.group')), width: '8rem', truncate: true });
    }
    if (this.isColumnEnabled('LAST_ACTIVE')) {
      columns.push({
        key: 'lastActiveOn',
        label: this.translate(marker('translations.global.label.lastActive')),
        sortable: true,
        width: '7.25rem',
      });
    }
    if (this.isColumnEnabled('JOIN_DATE')) {
      columns.push({
        key: 'createdAt',
        label: this.translate(marker('translations.global.label.joinDate')),
        sortable: true,
        width: '7.25rem',
      });
    }

    columns.push({ key: 'status', label: this.translate(marker('translations.global.label.status')), width: '6.5rem' });
    columns.push({ key: 'actions', label: '', align: 'end', width: '3rem' });

    this.tableColumns = columns;
  }

  private translate(key: string): string {
    return this.transloco.translate(key);
  }

  private syncTableSort(): void {
    this.tableSort = this.defaultOrderBy ? { key: this.defaultOrderBy, direction: this.order === 'DESC' ? 'desc' : 'asc' } : null;
  }

  private persistPagination(): void {
    const order: MembersSearchOrder = {
      fieldName: this.defaultOrderBy,
      direction: this.order,
    };
    const paginationFilter: PaginationFilter = {
      page: this.currentPage,
      pageSize: this.pageSize,
      order,
    };

    if (this.isCardPage) {
      this.store.dispatch(new UpdateCardPaginationFilters(this.cardUid, paginationFilter));
    } else if (this.isPlaylistPage) {
      this.store.dispatch(new UpdatePlaylistPaginationFilters(this.playlistUid, paginationFilter));
    } else {
      this.store.dispatch(new UpdateMemberPaginationFilters(paginationFilter));
    }
  }

  override openUserRecordByUid(member: Members): void {
    const memberInfoData: MemberInfoDialogData = {
      member: member,
      isPlaylistPage: this.isPlaylistPage,
      isCardPage: this.isCardPage,
      parentUrl: this.router.url,
      query: this.booleanSearchRequest,
    };

    const dialogRef = this.dialog.open<V3MemberInfoDialogComponent, MemberInfoDialogData>(V3MemberInfoDialogComponent, {
      width: '23.75rem',
      height: '100vh',
      disableClose: true,
      position: { right: '0', top: '0' },
      panelClass: ['slide-in-dialog', 'f_slide-in-dialog', 'member-info-dialog'],
      data: memberInfoData,
    });

    dialogRef
      .backdropClick()
      .pipe(takeUntil(dialogRef.afterClosed()))
      .subscribe(() => {
        const overlayPane = document.querySelector('.f_slide-in-dialog');

        if (overlayPane) {
          overlayPane.classList.add('slide-out');
        }

        setTimeout(() => {
          dialogRef.close();
        }, 300);
      });
  }
}
